import type { ColumnDef } from "@tanstack/react-table";
import { FiCopy, FiEye } from "react-icons/fi";

import type { BaseRowItem } from "../../components/data-table";
import type { InviteCodeApi } from "../../lib/api/inviteCodes";
import type { InviteCodeUiStatus } from "./inviteCodesTypes";

export const INVITE_CODES_TABLE_LIMIT = 25;

export type InviteCodeTableRow = BaseRowItem & {
  raw: InviteCodeApi;
  code: string;
  createdByLabel: string;
  inviterLabel: string | null;
  totalPts: number;
  depositPts: number;
  tradePts: number;
  minDepositLabel: string;
  minTradeLabel: string;
  pairsLabel: string;
  eligibleLabel: string;
  status: InviteCodeUiStatus;
  statusLabel: string;
  membersLabel: string;
  expiryLabel: string;
};

function ownerDisplayName(c: InviteCodeApi): string | null {
  const p = c.ownerUserId?.profile;
  if (p?.firstName || p?.lastName) {
    return [p.firstName, p.lastName].filter(Boolean).join(" ").trim();
  }
  const email = c.ownerUserId?.email?.address;
  return email || null;
}

function normalizeStatus(raw: string): InviteCodeUiStatus {
  const u = raw.toUpperCase();
  if (u === "ACTIVE") return "active";
  if (u === "USED") return "used";
  if (u === "EXPIRED") return "expired";
  if (u === "DEACTIVATED") return "deactivated";
  return "active";
}

function eligibilityLabel(v: string): string {
  const u = v.toUpperCase();
  if (u === "NEW_USERS") return "New Users";
  if (u === "EXISTING_USERS") return "Existing Users";
  if (u === "ALL") return "All Users";
  return v;
}

function formatOrdinalDate(iso: string | null | undefined): string {
  if (!iso) return "Never";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const day = d.getDate();
  const suffix =
    day % 10 === 1 && day !== 11
      ? "st"
      : day % 10 === 2 && day !== 12
      ? "nd"
      : day % 10 === 3 && day !== 13
      ? "rd"
      : "th";
  const month = d.toLocaleString("en-US", { month: "short" });
  return `${month} ${day}${suffix}, ${d.getFullYear()}`;
}

export function inviteStatusBadgeClass(status: InviteCodeUiStatus): string {
  switch (status) {
    case "active":
      return "bg-emerald-500/15 text-emerald-400 border-emerald-500/35";
    case "used":
      return "bg-sky-500/15 text-sky-300 border-sky-500/35";
    case "expired":
      return "bg-amber-500/15 text-amber-300 border-amber-500/35";
    case "deactivated":
      return "bg-[#2C363E] text-[#AEBCC6] border-[#3A4852]";
    default:
      return "bg-[#2C363E] text-[#AEBCC6] border-[#3A4852]";
  }
}

export function buildInviteCodeTableRows(
  codes: InviteCodeApi[]
): InviteCodeTableRow[] {
  return codes.map((c) => {
    const status = normalizeStatus(String(c.status));
    const inviter = ownerDisplayName(c);
    const members =
      c.stats?.members ?? c.memberCount ?? 0;
    return {
      id: c._id,
      raw: c,
      code: c.code,
      createdByLabel: c.createdBy
        ? `${String(c.createdBy).slice(0, 6)}…`
        : "—",
      inviterLabel: inviter,
      totalPts: (c.depositReward || 0) + (c.tradeReward || 0),
      depositPts: c.depositReward || 0,
      tradePts: c.tradeReward || 0,
      minDepositLabel: `Deposit: $${c.minDepositUSD}`,
      minTradeLabel: `Trade: $${c.minTradeUSD}`,
      pairsLabel: Array.isArray(c.requiredPairs)
        ? c.requiredPairs.join(", ")
        : "",
      eligibleLabel: eligibilityLabel(String(c.eligibility || "ALL")),
      status,
      statusLabel: status.charAt(0).toUpperCase() + status.slice(1),
      membersLabel:
        members > 0
          ? `${members} member${members === 1 ? "" : "s"}`
          : "—",
      expiryLabel: formatOrdinalDate(c.expiresAt),
    };
  });
}

type ColumnCallbacks = {
  onCopy: (code: string) => void;
  onView: (row: InviteCodeTableRow) => void;
};

export function createInviteCodeTableColumns({
  onCopy,
  onView,
}: ColumnCallbacks): ColumnDef<InviteCodeTableRow, unknown>[] {
  return [
    {
      accessorKey: "code",
      header: "Code",
      cell: ({ row }) => (
        <div className="min-w-[160px]">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-white">{row.original.code}</span>
            <button
              type="button"
              aria-label={`Copy ${row.original.code}`}
              onClick={() => onCopy(row.original.code)}
              className="rounded p-1 text-[#869AA9] transition hover:bg-[#273037] hover:text-white"
            >
              <FiCopy className="text-sm" />
            </button>
          </div>
          <p className="mt-1 text-[11px] text-[#869AA9]" title={row.original.raw.createdBy}>
            Created by {row.original.createdByLabel}
          </p>
          {row.original.inviterLabel && (
            <p className="mt-0.5 text-[11px] font-medium text-secondary_button">
              Inviter: {row.original.inviterLabel}
            </p>
          )}
        </div>
      ),
    },
    {
      accessorKey: "totalPts",
      header: "Reward",
      cell: ({ row }) => (
        <div>
          <p className="text-[14px] font-semibold text-white">
            {row.original.totalPts} pts
          </p>
          <p className="mt-1 text-[11px] text-[#869AA9]">
            Deposit: {row.original.depositPts} / Trade: {row.original.tradePts}
          </p>
        </div>
      ),
    },
    {
      id: "conditions",
      header: "Conditions",
      meta: { wrap: true, maxWidth: "220px" },
      cell: ({ row }) => (
        <div className="space-y-0.5 text-[12px] text-[#AEBCC6]">
          <p>{row.original.minDepositLabel}</p>
          <p>{row.original.minTradeLabel}</p>
          {row.original.pairsLabel ? (
            <p className="text-[#869AA9]">{row.original.pairsLabel}</p>
          ) : null}
          <p className="font-medium text-secondary_button">
            {row.original.eligibleLabel}
          </p>
        </div>
      ),
    },
    {
      accessorKey: "statusLabel",
      header: "Status",
      cell: ({ row }) => (
        <span
          className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold capitalize tracking-wide ${inviteStatusBadgeClass(
            row.original.status
          )}`}
        >
          {row.original.statusLabel}
        </span>
      ),
    },
    {
      accessorKey: "membersLabel",
      header: "Members",
      meta: { hideBelow: "md" as const },
      cell: ({ getValue }) => (
        <span className="text-adminTable-muted">{String(getValue())}</span>
      ),
    },
    {
      accessorKey: "expiryLabel",
      header: "Expiry",
      meta: { hideBelow: "lg" as const },
      cell: ({ getValue }) => (
        <span className="text-adminTable-muted">{String(getValue())}</span>
      ),
    },
    {
      id: "actions",
      header: "",
      meta: { align: "right" as const },
      cell: ({ row }) => (
        <button
          type="button"
          aria-label={`View ${row.original.code}`}
          onClick={() => onView(row.original)}
          className="rounded-lg border border-[#3A4852] bg-[#1C2227] p-2 text-[#AEBCC6] transition hover:border-secondary_button hover:text-white"
        >
          <FiEye className="text-base" />
        </button>
      ),
    },
  ];
}
